import PropTypes from 'prop-types';

export const POST_TRIAL_REGISTRATION = 'POST_TRIAL_REGISTRATION';
export const POST_TRIAL_REGISTRATION_START = `${POST_TRIAL_REGISTRATION}_START`;
export const POST_TRIAL_REGISTRATION_SUCCESS = `${POST_TRIAL_REGISTRATION}_SUCCESS`;
export const POST_TRIAL_REGISTRATION_FAIL = `${POST_TRIAL_REGISTRATION}_FAIL`;

export const GET_COMPANY_NAME_AVAILABILITY = 'GET_COMPANY_NAME_AVAILABILITY';
export const GET_COMPANY_NAME_AVAILABILITY_START = `${GET_COMPANY_NAME_AVAILABILITY}_START`;
export const GET_COMPANY_NAME_AVAILABILITY_SUCCESS = `${GET_COMPANY_NAME_AVAILABILITY}_SUCCESS`;
export const GET_COMPANY_NAME_AVAILABILITY_FAIL = `${GET_COMPANY_NAME_AVAILABILITY}_FAIL`;

export const GET_HOSTNAME_PREFIX_AVAILABILITY =
  'GET_HOSTNAME_PREFIX_AVAILABILITY';
export const GET_HOSTNAME_PREFIX_AVAILABILITY_START = `${GET_HOSTNAME_PREFIX_AVAILABILITY}_START`;
export const GET_HOSTNAME_PREFIX_AVAILABILITY_SUCCESS = `${GET_HOSTNAME_PREFIX_AVAILABILITY}_SUCCESS`;
export const GET_HOSTNAME_PREFIX_AVAILABILITY_FAIL = `${GET_HOSTNAME_PREFIX_AVAILABILITY}_FAIL`;

export const PING_DELAY_IN_MS = 1000;
const MAX_PING_ATTEMPTS = 60;

export const MIN_SUCCESSFUL_PINGS = 10;

export const AVAILABLE_STATUS = 'available';
export const UNAVAILABLE_STATUS = 'unavailable';
export const INVALID_STATUS = 'invalid';

export const trialRegistrationJsonPropType = PropTypes.shape({
  companyName: PropTypes.string,
  completed: PropTypes.bool,
  emailAddress: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  referralId: PropTypes.string,
});

export function postTrialRegistration(payload) {
  return {
    type: POST_TRIAL_REGISTRATION,
    meta: {
      xmWebApp: true,
      uri: '/xmatters/registration/api/web/1/registrationJson',
      method: 'post',
      params: payload,
    },
  };
}

export const createAvailabilityResponseHandler = resultProperty => response => {
  const { status } = response;
  const resultObject = {};
  // Treat 404 response as valid response that entity was not found (as per api)
  if (status === 404) {
    resultObject[resultProperty] = AVAILABLE_STATUS;
    return Promise.resolve(resultObject);
  }
  if (status === 400) {
    resultObject[resultProperty] = INVALID_STATUS;
    return Promise.resolve(resultObject);
  }
  resultObject[resultProperty] = UNAVAILABLE_STATUS;
  return Promise.resolve(resultObject);
};

export function getCompanyNameAvailability(companyName) {
  return {
    type: GET_COMPANY_NAME_AVAILABILITY,
    meta: {
      xmWebApp: true,
      uri: `/xmatters/registration/api/company-name/${companyName}`,
      method: 'get',
      responseHandler: createAvailabilityResponseHandler('companyNameStatus'),
    },
  };
}

export function getHostNamePrefixAvailability(hostnamePrefix) {
  return {
    type: GET_HOSTNAME_PREFIX_AVAILABILITY,
    meta: {
      xmWebApp: true,
      uri: `/xmatters/registration/api/hostname-prefix/${hostnamePrefix}`,
      method: 'get',
      responseHandler: createAvailabilityResponseHandler(
        'hostnamePrefixStatus',
      ),
    },
  };
}

export function pingTrialHost(pingUrl, successCallback, pingDelay) {
  return new Promise(resolve => {
    function pingTrialHostRecursive(retryAttemptCount = 0, successCount = 0) {
      const failureCallback = () => {
        // try again PING_DELAY_IN_MS/1000 seconds
        setTimeout(
          // increment retryAttemptCount, reset successCount
          () => {
            pingTrialHostRecursive(retryAttemptCount + 1, 0);
          },
          pingDelay ?? PING_DELAY_IN_MS,
        );
      };

      const successCounterCallback = () => {
        if (successCount + 1 >= MIN_SUCCESSFUL_PINGS) {
          successCallback();
          resolve();
        } else {
          setTimeout(
            // increment retryAttemptCount, increment successCount
            () => {
              pingTrialHostRecursive(retryAttemptCount + 1, successCount + 1);
            },
            pingDelay ?? PING_DELAY_IN_MS,
          );
        }
      };

      if (retryAttemptCount > MAX_PING_ATTEMPTS) {
        // After MAX_PING_ATTEMPTS attempts/failures (PING_DELAY_IN_MS/1000 * 60 ~1 minute) to ping host
        // ,just proceed
        successCallback();
        resolve();
      }

      fetch(pingUrl).then(successCounterCallback).catch(failureCallback);
    }

    pingTrialHostRecursive();
  });
}

/*
 * Function to redirect to new trial URL only after it responds to ping requests
 * It takes a bit for the load balander to setup a new sub-domain and we don't want to forward the
 * customer until we know it is up
 */
export function redirectToTrialUrl(trialRedirectUrl, pingDelay) {
  // Generate ping URL of new trial domain
  const parser = document.createElement('a');
  parser.href = trialRedirectUrl;
  // parser.origin does not work in all versions of IE
  const pingUrl = `${parser.protocol}//${parser.host}/ping`;

  const successCallback = () => {
    window.location.replace(trialRedirectUrl);
  };
  return pingTrialHost(pingUrl, successCallback, pingDelay);
}
